import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { createPortal } from "react-dom";
import {
  obtenerEstudiante,
  actualizarEstudiante,
  actualizarEducacionGeneral,
  actualizarExperienciaGeneral,
  eliminarEducacionGeneral,
  eliminarExperienciaGeneral,
  obtenerCatalogosEstudiantes,
  asignarReferente,
  agregarVehiculoEstudiante,
  eliminarVehiculoEstudiante,
  agregarLicenciaEstudiante,
  eliminarLicenciaEstudiante,
} from "../../../../services/estudiantesService";
import { buscar as buscarReferentes } from "../../../../services/referentesService";
import { crearEmpresa, listarEmpresas } from "../../../../services/empresasService";
import { nombreCompleto, normalizeEstudiante } from "../adapters/estudiantesAdapter";
import useUbicacionesEcuador from "../hooks/useUbicacionesEcuador";
import useHistorialAcademico from "../hooks/useHistorialAcademico";
import HistorialPanel from "../components/HistorialPanel";
import DocumentosTab from "../components/DocumentosTab";
import EntregablesEstudianteTab from "../components/EntregablesEstudianteTab";
import useDocumentosEstudiante from "../hooks/useDocumentosEstudiante";
import useCentrosListado from "../hooks/useCentrosListado";
import useFormacionForm from "../../formaciones/hooks/useFormacionForm";
import useFormacionesOptions from "../../formaciones/hooks/useFormacionesOptions";
import FormacionFormModal from "../../formaciones/components/FormacionFormModal";
import { usePermissions } from "../../../../utils/permissions";
import { showToast } from "../../../../utils/showToast";
import { soloDigitosCedula, esCedulaCompleta, mensajeErrorCedula } from "../../../../utils/documentoIdentidad";
import { normalizeEmpresa } from "../../../operativo/empresas/adapters/empresasAdapter";
import { DOCUMENTOS_EXT_PERMITIDAS, documentoLabel } from "../constants/documentos";
import "../../../../styles/dashboardTheme.css";
import "../../../../styles/managementTheme.css";
import "../styles/estudiantesDocumentos.css";
import "../styles/estudiantes.css";

const catalogosFallback = {
  estado_academico: ["preinscrito", "inscrito", "matriculado", "rechazado"],
  sexo: ["M", "F", "O"],
  estado_civil: ["soltero", "casado", "viudo", "divorciado", "union_libre"],
  tipo_sangre: ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"],
  tatuaje: ["no", "si_visible", "si_no_visible"],
  tipo_vehiculo: ["automovil", "bus", "camion", "camioneta", "furgoneta", "motocicleta", "trailer", "tricimoto"],
  licencia: ["A", "A1", "B", "C1", "C", "D1", "D", "E1", "E", "F", "G"],
  tipo_contrato: ["temporal", "indefinido", "practicante", "otro"],
  nivel_estudio: ["Educacion Basica", "Bachillerato", "Educacion Superior"],
};
// OJO: nunca usar new Date().toISOString().slice(0,10) para "hoy" — toISOString()
// convierte a UTC, y en Ecuador (UTC-5) desde las 19:00 ya marca el dia siguiente.
const fechaLocalHoy = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const toNumberOrNull = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
};
const normalizeSearchText = (value) => String(value || "").trim().toLowerCase();
const SEXO_LABELS = {
  M: "Masculino",
  F: "Femenino",
  O: "Otro",
};
const PAIS_ECUADOR = "ECUADOR";
const ESTADOS_ACADEMICOS_EDITABLES = new Set(["preinscrito", "inscrito", "rechazado"]);
const ESTADOS_ACADEMICOS_AUTOMATICOS = new Set(["matriculado", "egresado", "acreditado"]);

export default function EstudianteDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const computeTab = (search) => {
    const tabParam = new URLSearchParams(search || "").get("tab");
    const value = (tabParam || "").toLowerCase();
    return ["perfil", "domicilio", "movilidad", "salud", "formacion", "experiencia", "documentos", "entregables"].includes(value) ? value : "perfil";
  };
  const [estudiante, setEstudiante] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState(() => computeTab(location?.search));
  const [catalogos, setCatalogos] = useState(catalogosFallback);
  const [loadingCatalogos, setLoadingCatalogos] = useState(true);
  const { provincias, getCantones, getParroquias } = useUbicacionesEcuador();
  const historialAcademico = useHistorialAcademico(id);
  const documentosData = useDocumentosEstudiante(id);
  const { centros, loadingCentros } = useCentrosListado();
  const { hasPermission } = usePermissions();
  const canCrearFormacion = hasPermission("formaciones.crear");
  const canEditarFormacion = hasPermission("formaciones.actualizar");
  const canEliminarFormacion = hasPermission("formaciones.eliminar");
  const formacionForm = useFormacionForm({
    onCreated: () => historialAcademico.reloadHistorial(),
    onUpdated: () => historialAcademico.reloadHistorial(),
    onDeleted: () => historialAcademico.reloadHistorial(),
  });
  const {
    matriculasOptions: formacionMatriculasOptions,
    cursosOptions: formacionCursosOptions,
    loadingOptions: formacionLoadingOptions,
    reloadOptions: formacionReloadOptions,
  } = useFormacionesOptions();

  const [perfil, setPerfil] = useState({});
  const [domicilio, setDomicilio] = useState({ pais: PAIS_ECUADOR });
  const [movilidad, setMovilidad] = useState({});
  // Un estudiante puede tener varios vehículos y varias licencias (vigentes
  // o caducadas) — listas propias (estudiantes_vehiculos / estudiantes_licencias).
  const [vehiculos, setVehiculos] = useState([]);
  const [nuevoVehiculo, setNuevoVehiculo] = useState("");
  const [guardandoVehiculo, setGuardandoVehiculo] = useState(false);
  const [licencias, setLicencias] = useState([]);
  const [nuevaLicencia, setNuevaLicencia] = useState({ tipo_licencia: "", fecha_vencimiento: "" });
  const [guardandoLicencia, setGuardandoLicencia] = useState(false);
  const [salud, setSalud] = useState({});
  const [formacionInfo, setFormacionInfo] = useState({});
  const [educacionGeneralHistorial, setEducacionGeneralHistorial] = useState([]);
  const [editingEducacionId, setEditingEducacionId] = useState(null);
  const [educacionMenuOpenId, setEducacionMenuOpenId] = useState(null);
  const [educacionMenuPosition, setEducacionMenuPosition] = useState(null);
  const [educacionToDelete, setEducacionToDelete] = useState(null);
  const [experiencia, setExperiencia] = useState({});
  const [experienciaGeneralHistorial, setExperienciaGeneralHistorial] = useState([]);
  const [editingExperienciaId, setEditingExperienciaId] = useState(null);
  const [experienciaMenuOpenId, setExperienciaMenuOpenId] = useState(null);
  const [experienciaMenuPosition, setExperienciaMenuPosition] = useState(null);
  const [experienciaToDelete, setExperienciaToDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [referente, setReferente] = useState({ id: null, nombre: "", tipo: "" });
  const [referenteBusqueda, setReferenteBusqueda] = useState("");
  const [referenteOpciones, setReferenteOpciones] = useState([]);

  const [referenteGuardando, setReferenteGuardando] = useState(false);
  const [empresas, setEmpresas] = useState([]);
  const [loadingEmpresas, setLoadingEmpresas] = useState(false);
  const [experienciaEmpresaBusqueda, setExperienciaEmpresaBusqueda] = useState("");
  const [experienciaEmpresaMenuAbierto, setExperienciaEmpresaMenuAbierto] = useState(false);
  const carnetFileRef = useRef(null);
  const educacionMenuRef = useRef(null);
  const experienciaMenuRef = useRef(null);
  const hasCarnetTipoSangre = useMemo(
    () => (documentosData.documentos || []).some((doc) => doc.tipo === "carnet_tipo_sangre"),
    [documentosData.documentos]
  );
  /* const hasDocumentoIdentidad = useMemo(
    () => (documentosData.documentos || []).some((doc) => doc.tipo === "documento_identidad"),
    [documentosData.documentos]
  ); */

  const boolToDb = (value) => (value === undefined || value === null ? null : value ? 1 : 0);

  const hydrateFromParsed = useCallback((parsed, rawData = null) => {
    setEstudiante(parsed);
    setPerfil({
      nombres: parsed.nombres,
      apellidos: parsed.apellidos,
      documento_identidad: parsed.documento,
      celular: parsed.telefono,
      email: parsed.email,
      estado_academico: parsed.estadoAcademico,
      activo: parsed.activo,
      nacionalidad: parsed.nacionalidad || "",
      fecha_nacimiento: parsed.fechaNacimiento || "",
      sexo: parsed.sexo || "",
      estado_civil: parsed.estadoCivil || "",
      centro_id: parsed.centroId || "",
      telefono_fijo: parsed.telefonoFijo || "",
      contacto_emergencia_nombre: parsed.contactoEmergenciaNombre || "",
      contacto_emergencia_telefono: parsed.contactoEmergenciaTelefono || "",
    });
    setDomicilio({
      pais: PAIS_ECUADOR,
      provincia: parsed.provincia || "",
      canton: parsed.canton || "",
      parroquia: parsed.parroquia || "",
      direccion: parsed.direccion || "",
      codigo_postal: parsed.codigoPostal || "",
    });
    setMovilidad({
      movilizacion: parsed.movilizacion,
      disp_viajar: parsed.dispViajar,
      disp_turnos: parsed.dispTurnos,
      disp_fines_semana: parsed.dispFinesSemana,
    });
    setVehiculos(Array.isArray(parsed.vehiculos) ? parsed.vehiculos : []);
    setLicencias(Array.isArray(parsed.licencias) ? parsed.licencias : []);
    setSalud({
      tipo_sangre: parsed.tipoSangre || "",
      estatura: parsed.estatura || "",
      peso: parsed.peso || "",
      tatuaje: parsed.tatuaje || "",
    });
    setFormacionInfo({
      nivel_estudio: parsed.nivelEstudio || "",
      institucion: parsed.institucion || "",
      titulo_obtenido: parsed.tituloObtenido || "",
    });
    const historial = rawData?.educacion_general_historial || parsed.educacionGeneralHistorial || [];
    setEducacionGeneralHistorial(Array.isArray(historial) ? historial : []);
    const experienciaHistorial = rawData?.experiencia_historial || parsed.experienciaHistorial || [];
    setExperienciaGeneralHistorial(Array.isArray(experienciaHistorial) ? experienciaHistorial : []);
    const actualmenteTrabaja = parsed.experienciaActualmenteTrabaja === 1 || parsed.experienciaActualmenteTrabaja === "1" || parsed.experienciaActualmenteTrabaja === true;
    setExperiencia({
      experiencia_id: parsed.experienciaId || null,
      empresa_id: parsed.experienciaEmpresaId || "",
      cargo: parsed.experienciaCargo || "",
      fecha_inicio: parsed.experienciaFechaInicio || "",
      fecha_fin: actualmenteTrabaja ? "" : (parsed.experienciaFechaFin || ""),
      actualmente_trabaja: actualmenteTrabaja,
      tipo_contrato: parsed.experienciaTipoContrato || "",
      descripcion: parsed.experienciaDescripcion || "",
      provincia: parsed.experienciaProvincia || "",
      canton: parsed.experienciaCanton || "",
    });
    setReferente({
      id:     parsed.referenteId     || null,
      nombre: parsed.referenteNombre || "",
      tipo:   parsed.referenteTipo   || "",
    });
    setReferenteBusqueda(parsed.referenteNombre || "");
    setReferenteOpciones([]);
    setExperienciaEmpresaBusqueda("");
  }, []);

  useEffect(() => {
    const fetchCatalogos = async () => {
      setLoadingCatalogos(true);
      try {
        const data = await obtenerCatalogosEstudiantes();
        setCatalogos(data || catalogosFallback);
      } catch (err) {
        console.error(err);
        setCatalogos(catalogosFallback);
      } finally {
        setLoadingCatalogos(false);
      }
    };
    fetchCatalogos();
  }, []);

  useEffect(() => {
    const fetchEmpresas = async () => {
      setLoadingEmpresas(true);
      try {
        const data = await listarEmpresas();
        const lista = Array.isArray(data) ? data.map((e) => normalizeEmpresa(e)) : [];
        setEmpresas(lista);
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingEmpresas(false);
      }
    };
    fetchEmpresas();
  }, []);

  const experienciaEmpresaId = experiencia?.empresa_id;

  useEffect(() => {
    const empresaId = experienciaEmpresaId;
    if (!empresaId) return;
    const empresaSeleccionada = empresas.find((emp) => String(emp.id) === String(empresaId));
    if (!empresaSeleccionada?.nombre) return;
    setExperienciaEmpresaBusqueda((prev) => {
      if (normalizeSearchText(prev) === normalizeSearchText(empresaSeleccionada.nombre)) return prev;
      return empresaSeleccionada.nombre;
    });
  }, [empresas, experienciaEmpresaId]);

  const refrescarEstudiante = useCallback(async () => {
    const data = await obtenerEstudiante(id);
    const parsed = normalizeEstudiante(data, id);
    hydrateFromParsed(parsed, data);
    setError(null);
    return { data, parsed };
  }, [id, hydrateFromParsed]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        await refrescarEstudiante();
      } catch (err) {
        console.error(err);
        setError(err.message || "No se pudo cargar el estudiante.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [refrescarEstudiante]);

  useEffect(() => {
    setTab(computeTab(location?.search));
  }, [location.search]);

  const educacionMenuItem = useMemo(() => {
    if (!educacionMenuOpenId) return null;
    return (
      educacionGeneralHistorial.find((item) => Number(item.id) === Number(educacionMenuOpenId)) || null
    );
  }, [educacionGeneralHistorial, educacionMenuOpenId]);
  const experienciaMenuItem = useMemo(() => {
    if (!experienciaMenuOpenId) return null;
    return (
      experienciaGeneralHistorial.find((item) => Number(item.id) === Number(experienciaMenuOpenId)) || null
    );
  }, [experienciaGeneralHistorial, experienciaMenuOpenId]);
  const experienciaEmpresaMatches = useMemo(() => {
    const query = normalizeSearchText(experienciaEmpresaBusqueda);
    if (query.length < 3) return [];
    return empresas
      .filter((emp) => normalizeSearchText(emp.nombre).includes(query))
      .slice(0, 8);
  }, [empresas, experienciaEmpresaBusqueda]);
  const experienciaEmpresaHasExactMatch = useMemo(() => {
    const query = normalizeSearchText(experienciaEmpresaBusqueda);
    if (!query) return false;
    return empresas.some((emp) => normalizeSearchText(emp.nombre) === query);
  }, [empresas, experienciaEmpresaBusqueda]);

  useEffect(() => {
    const handleClickOutsideMenu = (event) => {
      const target = event.target;
      const inMenu = educacionMenuRef.current?.contains(target);
      const fromTrigger =
        target instanceof Element && target.closest(".educacion-menu-icon-trigger");
      if (!inMenu && !fromTrigger) {
        setEducacionMenuOpenId(null);
        setEducacionMenuPosition(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutsideMenu);
    return () => {
      document.removeEventListener("mousedown", handleClickOutsideMenu);
    };
  }, []);

  useEffect(() => {
    if (!educacionMenuOpenId) return undefined;
    const closeMenu = () => {
      setEducacionMenuOpenId(null);
      setEducacionMenuPosition(null);
    };
    window.addEventListener("scroll", closeMenu, true);
    window.addEventListener("resize", closeMenu);
    return () => {
      window.removeEventListener("scroll", closeMenu, true);
      window.removeEventListener("resize", closeMenu);
    };
  }, [educacionMenuOpenId]);
  useEffect(() => {
    const handleClickOutsideMenu = (event) => {
      const target = event.target;
      const inMenu = experienciaMenuRef.current?.contains(target);
      const fromTrigger =
        target instanceof Element && target.closest(".experiencia-menu-trigger");
      if (!inMenu && !fromTrigger) {
        setExperienciaMenuOpenId(null);
        setExperienciaMenuPosition(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutsideMenu);
    return () => {
      document.removeEventListener("mousedown", handleClickOutsideMenu);
    };
  }, []);

  useEffect(() => {
    if (!experienciaMenuOpenId) return undefined;
    const closeMenu = () => {
      setExperienciaMenuOpenId(null);
      setExperienciaMenuPosition(null);
    };
    window.addEventListener("scroll", closeMenu, true);
    window.addEventListener("resize", closeMenu);
    return () => {
      window.removeEventListener("scroll", closeMenu, true);
      window.removeEventListener("resize", closeMenu);
    };
  }, [experienciaMenuOpenId]);

  const toggleEducacionMenu = useCallback((event, educacionId) => {
    event.preventDefault();
    event.stopPropagation();

    const idNum = Number(educacionId);
    if (!Number.isInteger(idNum) || idNum <= 0) return;

    if (educacionMenuOpenId === idNum) {
      setEducacionMenuOpenId(null);
      setEducacionMenuPosition(null);
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const menuWidth = 168;
    const menuHeight = 102;
    const gap = 8;
    const viewportPadding = 8;

    let left = rect.right - menuWidth;
    left = Math.max(viewportPadding, Math.min(left, window.innerWidth - menuWidth - viewportPadding));

    let top = rect.bottom + gap;
    if (top + menuHeight > window.innerHeight - viewportPadding) {
      top = rect.top - menuHeight - gap;
    }
    top = Math.max(viewportPadding, top);

    setEducacionMenuOpenId(idNum);
    setEducacionMenuPosition({ top, left });
  }, [educacionMenuOpenId]);
  const toggleExperienciaMenu = useCallback((event, experienciaId) => {
    event.preventDefault();
    event.stopPropagation();

    const idNum = Number(experienciaId);
    if (!Number.isInteger(idNum) || idNum <= 0) return;

    if (experienciaMenuOpenId === idNum) {
      setExperienciaMenuOpenId(null);
      setExperienciaMenuPosition(null);
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const menuWidth = 168;
    const menuHeight = 102;
    const gap = 8;
    const viewportPadding = 8;

    let left = rect.right - menuWidth;
    left = Math.max(viewportPadding, Math.min(left, window.innerWidth - menuWidth - viewportPadding));

    let top = rect.bottom + gap;
    if (top + menuHeight > window.innerHeight - viewportPadding) {
      top = rect.top - menuHeight - gap;
    }
    top = Math.max(viewportPadding, top);

    setExperienciaMenuOpenId(idNum);
    setExperienciaMenuPosition({ top, left });
  }, [experienciaMenuOpenId]);

  const setTabAndQuery = (nextTab) => {
    setTab(nextTab);
    const search = nextTab === "perfil" ? "" : `?tab=${nextTab}`;
    navigate({ pathname: location.pathname, search }, { replace: true });
  };

  const cantones = useMemo(() => getCantones(domicilio.provincia), [domicilio.provincia, getCantones]);
  const parroquias = useMemo(() => getParroquias(domicilio.canton), [domicilio.canton, getParroquias]);
  const cantonesExperiencia = useMemo(() => getCantones(experiencia.provincia), [experiencia.provincia, getCantones]);
  const estadoAcademicoActual = String(perfil.estado_academico || "").trim().toLowerCase();
  const estadoAcademicoEsAutomatico = ESTADOS_ACADEMICOS_AUTOMATICOS.has(estadoAcademicoActual);
  const estadosAcademicosEditables = useMemo(() => {
    const source = Array.isArray(catalogos.estado_academico)
      ? catalogos.estado_academico
      : catalogosFallback.estado_academico;
    return source.filter((estado) => ESTADOS_ACADEMICOS_EDITABLES.has(String(estado || "").trim().toLowerCase()));
  }, [catalogos.estado_academico]);

  const buildPayload = useCallback(() => {
    return {
      // estudiantes + contacto
      nombres: perfil.nombres || "",
      apellidos: perfil.apellidos || "",
      documento_identidad: perfil.documento_identidad || "",
      estado_academico: perfil.estado_academico || "",
      activo: boolToDb(perfil.activo) ?? 0,
      nacionalidad: perfil.nacionalidad || null,
      fecha_nacimiento: perfil.fecha_nacimiento || null,
      sexo: perfil.sexo || null,
      estado_civil: perfil.estado_civil || null,
      centro_id: perfil.centro_id || null,
      email: perfil.email || null,
      celular: perfil.celular || null,
      telefono_fijo: perfil.telefono_fijo || null,
      contacto_emergencia_nombre: perfil.contacto_emergencia_nombre || null,
      contacto_emergencia_telefono: perfil.contacto_emergencia_telefono || null,
      // domicilio
      pais: PAIS_ECUADOR,
      provincia: domicilio.provincia || null,
      canton: domicilio.canton || null,
      parroquia: domicilio.parroquia || null,
      direccion: domicilio.direccion || null,
      codigo_postal: domicilio.codigo_postal || null,
      // salud
      tipo_sangre: salud.tipo_sangre || null,
      estatura: salud.estatura || null,
      peso: salud.peso || null,
      tatuaje: salud.tatuaje || null,
      // logistica (vehículos y licencias se gestionan aparte — pueden ser varios)
      movilizacion: boolToDb(movilidad.movilizacion),
      disp_viajar: boolToDb(movilidad.disp_viajar),
      disp_turnos: boolToDb(movilidad.disp_turnos),
      disp_fines_semana: boolToDb(movilidad.disp_fines_semana),
      // educacion general
      nivel_estudio: formacionInfo.nivel_estudio || null,
      institucion: formacionInfo.institucion || null,
      titulo_obtenido: formacionInfo.titulo_obtenido || null,
      // experiencia (soporta update si viene el id)
      experiencia_id: experiencia.experiencia_id || null,
      empresa_id: toNumberOrNull(experiencia.empresa_id),
      cargo: experiencia.cargo || null,
      fecha_inicio: experiencia.fecha_inicio || null,
      fecha_fin: experiencia.actualmente_trabaja ? null : (experiencia.fecha_fin || null),
      actualmente_trabaja: boolToDb(experiencia.actualmente_trabaja),
      tipo_contrato: experiencia.tipo_contrato || null,
      descripcion: experiencia.descripcion || null,
      exp_provincia: experiencia.actualmente_trabaja ? (experiencia.provincia || null) : null,
      exp_canton: experiencia.actualmente_trabaja ? (experiencia.canton || null) : null,
    };
  }, [perfil, domicilio, movilidad, salud, formacionInfo, experiencia]);

  const notifyEstudiantesUpdated = useCallback((updatedId) => {
    try {
      localStorage.setItem("estudiantes:lastUpdate", String(Date.now()));
    } catch (err) {
      console.warn("No se pudo actualizar el marcador de estudiantes", err);
    }
    window.dispatchEvent(new CustomEvent("estudiantes:updated", { detail: { id: updatedId } }));
  }, []);

  const handleCarnetUpload = useCallback(() => {
    if (!salud.tipo_sangre) {
      showToast({ type: "warning", message: "Selecciona el tipo de sangre para subir el carnet." });
      return;
    }
    carnetFileRef.current?.click();
  }, [salud.tipo_sangre]);

  const onCarnetFileChange = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const ok = await documentosData.handleSubir({
        file,
        tipo_documento: "carnet_tipo_sangre",
        descripcion: documentoLabel("carnet_tipo_sangre"),
      });
      if (ok && carnetFileRef.current) {
        carnetFileRef.current.value = "";
      }
    },
    [documentosData]
  );

  const handleGuardar = useCallback(async () => {
    if (!esCedulaCompleta(perfil.documento_identidad)) {
      showToast({
        type: "warning",
        message: `${mensajeErrorCedula(perfil.documento_identidad)} Corrígela en la pestaña Perfil.`,
      });
      return;
    }
    if (!perfil.fecha_nacimiento) {
      showToast({
        type: "warning",
        message: "Ingresa la fecha de nacimiento en la pestaña Perfil antes de guardar.",
      });
      return;
    }
    const nextEstado = (perfil.estado_academico || "").trim().toLowerCase();
    const prevEstado = (estudiante?.estadoAcademico || "").trim().toLowerCase();
    if (["inscrito", "matriculado"].includes(nextEstado) && prevEstado !== nextEstado) {
      /* const nivelEstudio = (formacionInfo.nivel_estudio || "").trim().toLowerCase();
      if (nivelEstudio !== "bachillerato") {
        showToast({ type: "warning", message: "El nivel de estudio debe ser Bachillerato." });
        return;
      } */
      /* if (!hasDocumentoIdentidad) {
        showToast({ type: "warning", message: `Sube el documento de identidad antes de cambiar a ${nextEstado}.` });
        return;
      } */
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      await actualizarEstudiante(id, payload);
      await refrescarEstudiante();
      notifyEstudiantesUpdated(id);
      showToast({ type: "success", message: "Guardado correctamente." });
    } catch (err) {
      console.error(err);
      showToast({ type: "danger", message: err.message || "No se pudo guardar." });
    } finally {
      setSaving(false);
    }
  }, [
    buildPayload,
    estudiante,
    id,
    /* hasDocumentoIdentidad, */
    notifyEstudiantesUpdated,
    perfil.documento_identidad,
    perfil.estado_academico,
    perfil.fecha_nacimiento,
    refrescarEstudiante,
  ]);

  const handleAgregarVehiculo = useCallback(async () => {
    if (!nuevoVehiculo) {
      showToast({ type: "warning", message: "Selecciona el tipo de vehículo." });
      return;
    }
    setGuardandoVehiculo(true);
    try {
      const creado = await agregarVehiculoEstudiante(id, { tipo_vehiculo: nuevoVehiculo });
      setVehiculos((prev) => [...prev, creado]);
      setNuevoVehiculo("");
      showToast({ type: "success", message: "Vehículo agregado." });
    } catch (err) {
      showToast({ type: "danger", message: err.message || "No se pudo agregar el vehículo." });
    } finally {
      setGuardandoVehiculo(false);
    }
  }, [id, nuevoVehiculo]);

  const handleEliminarVehiculo = useCallback(async (vehiculoId) => {
    try {
      await eliminarVehiculoEstudiante(id, vehiculoId);
      setVehiculos((prev) => prev.filter((v) => v.id !== vehiculoId));
      showToast({ type: "success", message: "Vehículo eliminado." });
    } catch (err) {
      showToast({ type: "danger", message: err.message || "No se pudo eliminar el vehículo." });
    }
  }, [id]);

  const handleAgregarLicencia = useCallback(async () => {
    if (!nuevaLicencia.tipo_licencia) {
      showToast({ type: "warning", message: "Selecciona el tipo de licencia." });
      return;
    }
    setGuardandoLicencia(true);
    try {
      const creada = await agregarLicenciaEstudiante(id, nuevaLicencia);
      setLicencias((prev) => [...prev, { ...creada, caducada: false }]);
      setNuevaLicencia({ tipo_licencia: "", fecha_vencimiento: "" });
      showToast({ type: "success", message: "Licencia agregada." });
    } catch (err) {
      showToast({ type: "danger", message: err.message || "No se pudo agregar la licencia." });
    } finally {
      setGuardandoLicencia(false);
    }
  }, [id, nuevaLicencia]);

  const handleEliminarLicencia = useCallback(async (licenciaId) => {
    try {
      await eliminarLicenciaEstudiante(id, licenciaId);
      setLicencias((prev) => prev.filter((l) => l.id !== licenciaId));
      showToast({ type: "success", message: "Licencia eliminada." });
    } catch (err) {
      showToast({ type: "danger", message: err.message || "No se pudo eliminar la licencia." });
    }
  }, [id]);

  const cancelarEdicionEducacion = useCallback(() => {
    const latest = Array.isArray(educacionGeneralHistorial) && educacionGeneralHistorial.length > 0
      ? educacionGeneralHistorial[0]
      : null;
    setFormacionInfo({
      nivel_estudio: latest?.nivel_estudio || latest?.nivelEstudio || "",
      institucion: latest?.institucion || "",
      titulo_obtenido: latest?.titulo_obtenido || latest?.tituloObtenido || "",
    });
    setEditingEducacionId(null);
    setEducacionMenuOpenId(null);
    setEducacionMenuPosition(null);
    setEducacionToDelete(null);
  }, [educacionGeneralHistorial]);

  const handleEditarEducacion = useCallback((item) => {
    if (!item) return;
    const educacionId = Number(item.id);
    if (!Number.isInteger(educacionId) || educacionId <= 0) {
      showToast({ type: "danger", message: "No se pudo identificar el registro para editar." });
      return;
    }
    setFormacionInfo({
      nivel_estudio: item.nivel_estudio || item.nivelEstudio || "",
      institucion: item.institucion || "",
      titulo_obtenido: item.titulo_obtenido || item.tituloObtenido || "",
    });
    setEditingEducacionId(educacionId);
    setEducacionMenuOpenId(null);
    setEducacionMenuPosition(null);
  }, []);

  const abrirEliminarEducacion = useCallback((item) => {
    const educacionId = Number(item?.id);
    if (!Number.isInteger(educacionId) || educacionId <= 0) {
      showToast({ type: "danger", message: "No se pudo identificar el registro para eliminar." });
      return;
    }
    setEducacionToDelete(item);
    setEducacionMenuOpenId(null);
    setEducacionMenuPosition(null);
  }, []);

  const confirmarEliminarEducacion = useCallback(async () => {
    const educacionId = Number(educacionToDelete?.id);
    if (!Number.isInteger(educacionId) || educacionId <= 0) {
      showToast({ type: "danger", message: "No se pudo identificar el registro para eliminar." });
      setEducacionToDelete(null);
      return;
    }

    setSaving(true);
    try {
      await eliminarEducacionGeneral(id, educacionId);
      await refrescarEstudiante();
      if (editingEducacionId === educacionId) {
        setEditingEducacionId(null);
      }
      setEducacionToDelete(null);
      setEducacionMenuOpenId(null);
      setEducacionMenuPosition(null);
      showToast({ type: "success", message: "Registro eliminado correctamente." });
    } catch (err) {
      console.error(err);
      showToast({ type: "danger", message: err.message || "No se pudo eliminar el registro." });
    } finally {
      setSaving(false);
    }
  }, [educacionToDelete?.id, editingEducacionId, id, refrescarEstudiante]);

  const handleGuardarFormacion = useCallback(async () => {
    const payload = {
      nivel_estudio: formacionInfo.nivel_estudio || null,
      institucion: formacionInfo.institucion || null,
      titulo_obtenido: formacionInfo.titulo_obtenido || null,
    };

    if (editingEducacionId) {
      const educacionId = Number(editingEducacionId);
      if (!Number.isInteger(educacionId) || educacionId <= 0) {
        showToast({ type: "danger", message: "No se pudo identificar el registro para actualizar." });
        return;
      }

      setSaving(true);
      try {
        await actualizarEducacionGeneral(id, educacionId, payload);
        await refrescarEstudiante();
        setEditingEducacionId(null);
        setEducacionMenuOpenId(null);
        setEducacionMenuPosition(null);
        setEducacionToDelete(null);
        showToast({ type: "success", message: "Registro actualizado correctamente." });
      } catch (err) {
        console.error(err);
        showToast({ type: "danger", message: err.message || "No se pudo actualizar el registro." });
      } finally {
        setSaving(false);
      }
      return;
    }

    await handleGuardar();
  }, [editingEducacionId, formacionInfo.institucion, formacionInfo.nivel_estudio, formacionInfo.titulo_obtenido, handleGuardar, id, refrescarEstudiante]);

  const cancelarEdicionExperiencia = useCallback(() => {
    const latest = Array.isArray(experienciaGeneralHistorial) && experienciaGeneralHistorial.length > 0
      ? experienciaGeneralHistorial[0]
      : null;
    const currentlyTrabaja = latest?.actualmente_trabaja === 1 || latest?.actualmente_trabaja === "1" || latest?.actualmente_trabaja === true;
    const empresaId = latest?.empresa_id || latest?.empresaId || "";
    const empresaNombre = empresaId
      ? (empresas.find((emp) => String(emp.id) === String(empresaId))?.nombre || "")
      : "";
    setExperiencia({
      experiencia_id: latest?.id || null,
      empresa_id: empresaId,
      cargo: latest?.cargo || "",
      fecha_inicio: latest?.fecha_inicio ? String(latest.fecha_inicio).split("T")[0] : "",
      fecha_fin: currentlyTrabaja ? "" : (latest?.fecha_fin ? String(latest.fecha_fin).split("T")[0] : ""),
      actualmente_trabaja: currentlyTrabaja,
      tipo_contrato: latest?.tipo_contrato || "",
      descripcion: latest?.descripcion || "",
      provincia: latest?.provincia || "",
      canton: latest?.canton || "",
    });
    setExperienciaEmpresaBusqueda(empresaNombre);
    setEditingExperienciaId(null);
    setExperienciaMenuOpenId(null);
    setExperienciaMenuPosition(null);
    setExperienciaToDelete(null);
  }, [empresas, experienciaGeneralHistorial]);

  const handleEditarExperiencia = useCallback((item) => {
    if (!item) return;
    const experienciaId = Number(item.id);
    if (!Number.isInteger(experienciaId) || experienciaId <= 0) {
      showToast({ type: "danger", message: "No se pudo identificar el registro para editar." });
      return;
    }
    const currentlyTrabaja = item.actualmente_trabaja === 1 || item.actualmente_trabaja === "1" || item.actualmente_trabaja === true;
    const empresaId = item.empresa_id || item.empresaId || "";
    const empresaNombre = empresaId
      ? (empresas.find((emp) => String(emp.id) === String(empresaId))?.nombre || "")
      : "";
    setExperiencia({
      experiencia_id: experienciaId,
      empresa_id: empresaId,
      cargo: item.cargo || "",
      fecha_inicio: item.fecha_inicio ? String(item.fecha_inicio).split("T")[0] : "",
      fecha_fin: currentlyTrabaja ? "" : (item.fecha_fin ? String(item.fecha_fin).split("T")[0] : ""),
      actualmente_trabaja: currentlyTrabaja,
      tipo_contrato: item.tipo_contrato || "",
      descripcion: item.descripcion || "",
      provincia: item.provincia || "",
      canton: item.canton || "",
    });
    setExperienciaEmpresaBusqueda(empresaNombre);
    setEditingExperienciaId(experienciaId);
    setExperienciaMenuOpenId(null);
    setExperienciaMenuPosition(null);
  }, [empresas]);

  const abrirEliminarExperiencia = useCallback((item) => {
    const experienciaId = Number(item?.id);
    if (!Number.isInteger(experienciaId) || experienciaId <= 0) {
      showToast({ type: "danger", message: "No se pudo identificar el registro para eliminar." });
      return;
    }
    setExperienciaToDelete(item);
    setExperienciaMenuOpenId(null);
    setExperienciaMenuPosition(null);
  }, []);

  const confirmarEliminarExperiencia = useCallback(async () => {
    const experienciaId = Number(experienciaToDelete?.id);
    if (!Number.isInteger(experienciaId) || experienciaId <= 0) {
      showToast({ type: "danger", message: "No se pudo identificar el registro para eliminar." });
      setExperienciaToDelete(null);
      return;
    }

    setSaving(true);
    try {
      await eliminarExperienciaGeneral(id, experienciaId);
      await refrescarEstudiante();
      if (editingExperienciaId === experienciaId) {
        setEditingExperienciaId(null);
      }
      setExperienciaToDelete(null);
      setExperienciaMenuOpenId(null);
      setExperienciaMenuPosition(null);
      showToast({ type: "success", message: "Registro de experiencia eliminado correctamente." });
    } catch (err) {
      console.error(err);
      showToast({ type: "danger", message: err.message || "No se pudo eliminar el registro de experiencia." });
    } finally {
      setSaving(false);
    }
  }, [editingExperienciaId, experienciaToDelete?.id, id, refrescarEstudiante]);

  const handleGuardarExperiencia = useCallback(async () => {
    setSaving(true);
    try {
      const empresaBusqueda = (experienciaEmpresaBusqueda || "").trim();
      let empresaId = toNumberOrNull(experiencia.empresa_id);

      if (!empresaId && empresaBusqueda) {
        const empresaExistente = empresas.find(
          (emp) => normalizeSearchText(emp.nombre) === normalizeSearchText(empresaBusqueda)
        );

        if (empresaExistente?.id) {
          empresaId = toNumberOrNull(empresaExistente.id);
        } else {
          if (empresaBusqueda.length < 3) {
            throw new Error("Ingresa al menos 3 caracteres para crear una nueva empresa.");
          }
          const creada = await crearEmpresa({
            nombre: empresaBusqueda,
            tipo: "externa",
            activo: 1,
          });
          const empresaNormalizada = normalizeEmpresa(creada);
          if (!empresaNormalizada?.id) {
            throw new Error("No se pudo crear la empresa.");
          }
          setEmpresas((prev) => {
            if (prev.some((emp) => String(emp.id) === String(empresaNormalizada.id))) return prev;
            return [empresaNormalizada, ...prev];
          });
          empresaId = toNumberOrNull(empresaNormalizada.id);
          setExperienciaEmpresaBusqueda(empresaNormalizada.nombre || empresaBusqueda);
          setExperiencia((ex) => ({ ...ex, empresa_id: empresaNormalizada.id }));
        }
      }

      const payload = {
        empresa_id: empresaId,
        cargo: experiencia.cargo || null,
        fecha_inicio: experiencia.fecha_inicio || null,
        fecha_fin: experiencia.actualmente_trabaja ? null : (experiencia.fecha_fin || null),
        actualmente_trabaja: boolToDb(experiencia.actualmente_trabaja),
        tipo_contrato: experiencia.tipo_contrato || null,
        descripcion: experiencia.descripcion || null,
        provincia: experiencia.actualmente_trabaja ? (experiencia.provincia || null) : null,
        canton: experiencia.actualmente_trabaja ? (experiencia.canton || null) : null,
      };

      if (editingExperienciaId) {
        const experienciaId = Number(editingExperienciaId);
        if (!Number.isInteger(experienciaId) || experienciaId <= 0) {
          showToast({ type: "danger", message: "No se pudo identificar el registro para actualizar." });
          return;
        }

        await actualizarExperienciaGeneral(id, experienciaId, payload);
        await refrescarEstudiante();
        setExperiencia({
          experiencia_id: null,
          empresa_id: "",
          cargo: "",
          fecha_inicio: "",
          fecha_fin: "",
          actualmente_trabaja: false,
          tipo_contrato: "",
          descripcion: "",
          provincia: "",
          canton: "",
        });
        setExperienciaEmpresaBusqueda("");
        setExperienciaEmpresaMenuAbierto(false);
        setEditingExperienciaId(null);
        setExperienciaMenuOpenId(null);
        setExperienciaMenuPosition(null);
        setExperienciaToDelete(null);
        showToast({ type: "success", message: "Experiencia actualizada correctamente." });
        return;
      }

      const latest = Array.isArray(experienciaGeneralHistorial) && experienciaGeneralHistorial.length > 0
        ? experienciaGeneralHistorial[0]
        : null;
      const latestPayload = latest
        ? {
            empresa_id: toNumberOrNull(latest.empresa_id ?? latest.empresaId ?? null),
            cargo: latest.cargo || null,
            fecha_inicio: latest.fecha_inicio ? String(latest.fecha_inicio).split("T")[0] : null,
            fecha_fin: latest.actualmente_trabaja === 1 || latest.actualmente_trabaja === "1" || latest.actualmente_trabaja === true
              ? null
              : (latest.fecha_fin ? String(latest.fecha_fin).split("T")[0] : null),
            actualmente_trabaja: boolToDb(latest.actualmente_trabaja === 1 || latest.actualmente_trabaja === "1" || latest.actualmente_trabaja === true),
            tipo_contrato: latest.tipo_contrato || null,
            descripcion: latest.descripcion || null,
            provincia: latest.provincia || null,
            canton: latest.canton || null,
          }
        : null;

      if (
        latestPayload &&
        latestPayload.empresa_id === payload.empresa_id &&
        latestPayload.cargo === payload.cargo &&
        latestPayload.fecha_inicio === payload.fecha_inicio &&
        latestPayload.fecha_fin === payload.fecha_fin &&
        latestPayload.actualmente_trabaja === payload.actualmente_trabaja &&
        latestPayload.tipo_contrato === payload.tipo_contrato &&
        latestPayload.descripcion === payload.descripcion &&
        latestPayload.provincia === payload.provincia &&
        latestPayload.canton === payload.canton
      ) {
        showToast({ type: "warning", message: "No hay cambios para crear un nuevo registro de experiencia." });
        return;
      }

      const fullPayload = {
        ...buildPayload(),
        experiencia_id: null,
        ...payload,
      };
      await actualizarEstudiante(id, fullPayload);
      await refrescarEstudiante();
      setExperiencia({
        experiencia_id: null,
        empresa_id: "",
        cargo: "",
        fecha_inicio: "",
        fecha_fin: "",
        actualmente_trabaja: false,
        tipo_contrato: "",
        descripcion: "",
        provincia: "",
        canton: "",
      });
      setExperienciaEmpresaBusqueda("");
      setExperienciaEmpresaMenuAbierto(false);
      notifyEstudiantesUpdated(id);
      showToast({ type: "success", message: "Experiencia registrada correctamente." });
    } catch (err) {
      console.error(err);
      showToast({ type: "danger", message: err.message || "No se pudo guardar la experiencia." });
    } finally {
      setSaving(false);
    }
  }, [
    buildPayload,
    editingExperienciaId,
    empresas,
    experiencia.actualmente_trabaja,
    experiencia.cargo,
    experiencia.descripcion,
    experiencia.empresa_id,
    experienciaEmpresaBusqueda,
    experiencia.fecha_fin,
    experiencia.fecha_inicio,
    experiencia.tipo_contrato,
    experiencia.provincia,
    experiencia.canton,
    experienciaGeneralHistorial,
    id,
    notifyEstudiantesUpdated,
    refrescarEstudiante
  ]);

  const renderPerfil = () => (
    <form
      className="row g-3"
      onSubmit={(e) => {
        e.preventDefault();
        handleGuardar();
      }}
    >
      <div className="col-md-4">
        <label className="form-label">Nombres</label>
        <input className="form-control" placeholder="Ej: Juan Carlos" value={perfil.nombres || ""} onChange={(e) => setPerfil((p) => ({ ...p, nombres: e.target.value.toUpperCase() }))} />
      </div>
      <div className="col-md-4">
        <label className="form-label">Apellidos</label>
        <input className="form-control" placeholder="Ej: Perez Gomez" value={perfil.apellidos || ""} onChange={(e) => setPerfil((p) => ({ ...p, apellidos: e.target.value.toUpperCase() }))} />
      </div>
      <div className="col-md-4">
        <label className="form-label">Cédula</label>
        <input
          className="form-control"
          placeholder="Ej: 1712345678"
          inputMode="numeric"
          maxLength={10}
          value={perfil.documento_identidad || ""}
          onChange={(e) => setPerfil((p) => ({ ...p, documento_identidad: soloDigitosCedula(e.target.value) }))}
        />
      </div>
      <div className="col-md-4">
        <label className="form-label">Fecha de nacimiento <span className="text-danger">*</span></label>
        <input
          type="date"
          className="form-control"
          placeholder="AAAA-MM-DD"
          value={perfil.fecha_nacimiento || ""}
          max={fechaLocalHoy()}
          onChange={(e) => setPerfil((p) => ({ ...p, fecha_nacimiento: e.target.value }))}
        />
      </div>
      <div className="col-md-4">
        <label className="form-label">Nacionalidad</label>
        <input className="form-control" placeholder="Ej: Ecuatoriana" value={perfil.nacionalidad || ""} onChange={(e) => setPerfil((p) => ({ ...p, nacionalidad: e.target.value }))} />
      </div>
      <div className="col-md-4">
        <label className="form-label">Celular</label>
        <input className="form-control" placeholder="Ej: 0991234567" value={perfil.celular || ""} onChange={(e) => setPerfil((p) => ({ ...p, celular: e.target.value }))} />
      </div>
      <div className="col-md-4">
        <label className="form-label">Teléfono fijo</label>
        <input className="form-control" placeholder="Ej: 022345678" value={perfil.telefono_fijo || ""} onChange={(e) => setPerfil((p) => ({ ...p, telefono_fijo: e.target.value }))} />
      </div>
      <div className="col-md-4">
        <label className="form-label">Email</label>
        <input type="email" className="form-control" placeholder="juanperez@ademy.com" value={perfil.email || ""} onChange={(e) => setPerfil((p) => ({ ...p, email: e.target.value }))} />
      </div>
      <div className="col-md-4">
        <label className="form-label">Estado civil</label>
        <select className="form-select" value={perfil.estado_civil || ""} onChange={(e) => setPerfil((p) => ({ ...p, estado_civil: e.target.value }))} disabled={loadingCatalogos}>
          <option value="">Selecciona</option>
          {(catalogos.estado_civil || []).map((opt) => (
            <option key={`ec-${opt}`} value={opt}>
              {opt.replace("_", " ")}
            </option>
          ))}
        </select>
      </div>
      <div className="col-md-4">
        <label className="form-label">Contacto de emergencia (nombre)</label>
        <input className="form-control" placeholder="Ej: Maria Lopez" value={perfil.contacto_emergencia_nombre || ""} onChange={(e) => setPerfil((p) => ({ ...p, contacto_emergencia_nombre: e.target.value.toUpperCase() }))} />
      </div>
      <div className="col-md-4">
        <label className="form-label">Contacto de emergencia (teléfono)</label>
        <input className="form-control" placeholder="Ej: 0987654321" value={perfil.contacto_emergencia_telefono || ""} onChange={(e) => setPerfil((p) => ({ ...p, contacto_emergencia_telefono: e.target.value }))} />
      </div>
      <div className="col-md-4">
        <label className="form-label">Centro</label>
        <select
          className="form-select"
          value={perfil.centro_id || ""}
          onChange={(e) => setPerfil((p) => ({ ...p, centro_id: e.target.value }))}
          disabled={loadingCentros}
        >
          <option value="">Selecciona un centro</option>
          {centros.map((c) => (
            <option key={`centro-${c.id}`} value={c.id}>
              {c.nombre}
            </option>
          ))}
        </select>
      </div>
      <div className="col-md-4">
        <label className="form-label">Estado academico</label>
        <select
          className="form-select"
          value={perfil.estado_academico || ""}
          onChange={(e) => setPerfil((p) => ({ ...p, estado_academico: e.target.value }))}
          disabled={loadingCatalogos || estadoAcademicoEsAutomatico}
        >
          <option value="">Selecciona</option>
          {estadoAcademicoEsAutomatico ? (
            <option value={perfil.estado_academico}>
              {`${perfil.estado_academico} (automatico)`}
            </option>
          ) : null}
          {estadosAcademicosEditables.map((estado) => (
            <option key={estado} value={estado}>
              {estado}
            </option>
          ))}
        </select>
        <small className="text-muted d-block mt-1">
          {estadoAcademicoEsAutomatico
            ? "Este estado se calcula automaticamente desde matriculas/pagos."
            : "Aqui solo se cambia entre preinscrito, inscrito y rechazado."}
        </small>
      </div>
      <div className="col-md-4 d-flex align-items-center pt-4">
        <div className="form-check">
          <input className="form-check-input" type="checkbox" id="perfil-activo" checked={!!perfil.activo} onChange={(e) => setPerfil((p) => ({ ...p, activo: e.target.checked }))} />
          <label className="form-check-label" htmlFor="perfil-activo">
            Activo
          </label>
        </div>
      </div>
      {/* ── Referente / Comisionista ────────────────────────────── */}
      <div className="col-12">
        <hr className="my-1" />
        <label className="form-label fw-semibold">Referente / Comisionista</label>
        <div className="d-flex gap-2 align-items-start">
          <div className="position-relative flex-grow-1">
            <input
              type="text"
              className="form-control"
              placeholder="Buscar por nombre o identificación…"
              value={referenteBusqueda}
              onChange={async (e) => {
                const term = e.target.value;
                setReferenteBusqueda(term);
                if (referente.id && term !== referente.nombre) {
                  setReferente({ id: null, nombre: "", tipo: "" });
                }
                if (term.length < 2) { setReferenteOpciones([]); return; }
                try {
                  const data = await buscarReferentes(term);
                  setReferenteOpciones(Array.isArray(data) ? data : []);
                } catch { setReferenteOpciones([]); }
              }}
            />
            {referenteOpciones.length > 0 && (
              <ul className="list-group position-absolute w-100 shadow-sm" style={{ zIndex: 1050, top: "100%" }}>
                {referenteOpciones.slice(0, 8).map((op) => (
                  <li
                    key={op.id}
                    className="list-group-item list-group-item-action py-2 px-3"
                    style={{ cursor: "pointer" }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setReferente({ id: op.id, nombre: op.nombre, tipo: op.tipo || "" });
                      setReferenteBusqueda(op.nombre);
                      setReferenteOpciones([]);
                    }}
                  >
                    <span className="fw-medium">{op.nombre}</span>
                    {op.tipo && (
                      <span className="badge bg-secondary ms-2 text-capitalize" style={{ fontSize: "0.7rem" }}>
                        {op.tipo}
                      </span>
                    )}
                    {op.identificacion && (
                      <span className="text-muted ms-2" style={{ fontSize: "0.8rem" }}>{op.identificacion}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
          {referente.id && (
            <button
              type="button"
              className="btn btn-outline-secondary"
              title="Quitar referente"
              onClick={() => {
                setReferente({ id: null, nombre: "", tipo: "" });
                setReferenteBusqueda("");
                setReferenteOpciones([]);
              }}
            >
              <i className="bi bi-x-lg" />
            </button>
          )}
          <button
            type="button"
            className="btn btn-outline-primary"
            disabled={referenteGuardando || (!referente.id && !referenteBusqueda && !estudiante?.referenteId)}
            onClick={async () => {
              setReferenteGuardando(true);
              try {
                await asignarReferente(id, referente.id || null);
                await refrescarEstudiante();
                showToast({ type: "success", message: referente.id ? "Referente asignado." : "Referente quitado." });
              } catch (err) {
                showToast({ type: "danger", message: err?.message || "Error al asignar referente." });
              } finally {
                setReferenteGuardando(false);
              }
            }}
          >
            {referenteGuardando ? "Guardando…" : "Asignar"}
          </button>
        </div>
        {referente.id ? (
          <small className="text-success mt-1 d-block">
            <i className="bi bi-check-circle me-1" />
            {referente.nombre}
            {referente.tipo && <span className="text-muted ms-1">({referente.tipo})</span>}
          </small>
        ) : (
          <small className="text-muted mt-1 d-block">Sin referente asignado.</small>
        )}
      </div>

      <div className="col-12 text-end">
        <button className="btn btn-primary" type="submit" disabled={saving}>
          {saving ? "Guardando..." : "Guardar"}
        </button>
      </div>
    </form>
  );

  const renderDomicilio = () => (
    <form
      className="row g-3"
      onSubmit={(e) => {
        e.preventDefault();
        handleGuardar();
      }}
    >
      <div className="col-md-4">
        <label className="form-label">Pais</label>
        <select className="form-select" value={PAIS_ECUADOR} disabled>
          <option value={PAIS_ECUADOR}>{PAIS_ECUADOR}</option>
        </select>
      </div>
      <div className="col-md-4">
        <label className="form-label">Provincia</label>
        <select
          className="form-select"
          value={domicilio.provincia || ""}
          onChange={(e) =>
            setDomicilio((d) => ({
              ...d,
              provincia: e.target.value,
              canton: "",
              parroquia: "",
              codigo_postal: "",
            }))
          }
        >
          <option value="">Seleccione provincia</option>
          {provincias.map((prov) => (
            <option key={`prov-${prov.code}`} value={prov.name}>
              {prov.name}
            </option>
          ))}
        </select>
      </div>
      <div className="col-md-4">
        <label className="form-label">Canton</label>
        <select
          className="form-select"
          value={domicilio.canton || ""}
          onChange={(e) => {
            const selected = cantones.find((c) => c.name === e.target.value);
            setDomicilio((d) => ({
              ...d,
              canton: e.target.value,
              parroquia: "",
              codigo_postal: selected?.code || "",
            }));
          }}
          disabled={!domicilio.provincia}
        >
          <option value="">Seleccione canton</option>
          {cantones.map((c) => (
            <option key={`canton-${c.code}-${c.name}`} value={c.name}>
              {c.name}
            </option>
          ))}
        </select>
      </div>
      <div className="col-md-4">
        <label className="form-label">Parroquia</label>
        <select
          className="form-select"
          value={domicilio.parroquia || ""}
          onChange={(e) => {
            const selected = parroquias.find((p) => p.name === e.target.value);
            setDomicilio((d) => ({
              ...d,
              parroquia: e.target.value,
              codigo_postal: selected?.code || d.codigo_postal,
            }));
          }}
          disabled={!domicilio.canton}
        >
          <option value="">Seleccione parroquia</option>
          {parroquias.map((p) => (
            <option key={`parroquia-${p.code}-${p.name}`} value={p.name}>
              {p.name}
            </option>
          ))}
        </select>
      </div>
      <div className="col-md-6">
        <label className="form-label">Direccion</label>
        <input className="form-control" placeholder="Calle principal y referencia" value={domicilio.direccion || ""} onChange={(e) => setDomicilio((d) => ({ ...d, direccion: e.target.value }))} />
      </div>
      <div className="col-md-2">
        <label className="form-label">Codigo postal</label>
        <input className="form-control" placeholder="Ej: 170150" value={domicilio.codigo_postal || ""} onChange={(e) => setDomicilio((d) => ({ ...d, codigo_postal: e.target.value }))} />
      </div>
      <div className="col-12 text-end">
        <button className="btn btn-primary" type="submit" disabled={saving}>
          {saving ? "Guardando..." : "Guardar"}
        </button>
      </div>
    </form>
  );

  const renderMovilidad = () => (
    <div>
      {/* Vehículos — un estudiante puede tener varios */}
      <h6 className="mb-2">Vehículos</h6>
      <div className="d-flex flex-wrap gap-2 mb-2">
        {vehiculos.length ? vehiculos.map((v) => (
          <span key={v.id} className="badge text-bg-light border d-inline-flex align-items-center gap-2 py-2 px-3">
            {(v.tipo_vehiculo || "").charAt(0).toUpperCase() + (v.tipo_vehiculo || "").slice(1)}
            <button type="button" className="btn-close" style={{ fontSize: 10 }} aria-label="Eliminar"
              onClick={() => handleEliminarVehiculo(v.id)} />
          </span>
        )) : <span className="text-muted small">Sin vehículos registrados.</span>}
      </div>
      <div className="d-flex gap-2 align-items-center mb-4">
        <select className="form-select" style={{ maxWidth: 240 }} value={nuevoVehiculo}
          onChange={(e) => setNuevoVehiculo(e.target.value)} aria-label="Tipo de vehiculo">
          <option value="">Tipo de vehiculo</option>
          {(catalogos.tipo_vehiculo || []).filter((opt) => !vehiculos.some((v) => v.tipo_vehiculo === opt)).map((opt) => (
            <option key={`veh-${opt}`} value={opt}>{opt.charAt(0).toUpperCase() + opt.slice(1)}</option>
          ))}
        </select>
        <button type="button" className="btn btn-outline-primary" disabled={guardandoVehiculo} onClick={handleAgregarVehiculo}>
          {guardandoVehiculo ? "Agregando..." : "Agregar"}
        </button>
      </div>

      {/* Licencias — varias, vigentes o caducadas */}
      <h6 className="mb-2">Licencias de conducir</h6>
      <div className="d-flex flex-column gap-2 mb-2">
        {licencias.length ? licencias.map((l) => (
          <div key={l.id} className="d-flex align-items-center gap-2 border rounded p-2 px-3" style={{ maxWidth: 480 }}>
            <span className="fw-semibold">Tipo {l.tipo_licencia}</span>
            {l.fecha_vencimiento && <span className="small text-muted">Vence: {String(l.fecha_vencimiento).slice(0, 10)}</span>}
            <span className={`badge text-bg-${l.caducada ? "danger" : "success"} ms-1`}>
              {l.caducada ? "Caducada" : "Vigente"}
            </span>
            <button type="button" className="btn-close ms-auto" style={{ fontSize: 10 }} aria-label="Eliminar"
              onClick={() => handleEliminarLicencia(l.id)} />
          </div>
        )) : <span className="text-muted small">Sin licencias registradas.</span>}
      </div>
      <div className="d-flex gap-2 align-items-end mb-4">
        <div>
          <label className="form-label small mb-1">Tipo</label>
          <select className="form-select" value={nuevaLicencia.tipo_licencia}
            onChange={(e) => setNuevaLicencia((s) => ({ ...s, tipo_licencia: e.target.value }))} aria-label="Licencia">
            <option value="">Licencia</option>
            {(catalogos.licencia || []).filter((opt) => !licencias.some((l) => l.tipo_licencia === opt)).map((opt) => <option key={`lic-${opt}`} value={opt}>{opt}</option>)}
          </select>
        </div>
        <div>
          <label className="form-label small mb-1">Fecha de vencimiento (opcional)</label>
          <input type="date" className="form-control" value={nuevaLicencia.fecha_vencimiento}
            onChange={(e) => setNuevaLicencia((s) => ({ ...s, fecha_vencimiento: e.target.value }))} />
        </div>
        <button type="button" className="btn btn-outline-primary" disabled={guardandoLicencia} onClick={handleAgregarLicencia}>
          {guardandoLicencia ? "Agregando..." : "Agregar"}
        </button>
      </div>

      {/* Disponibilidad — un solo valor por estudiante, se guarda con el formulario general */}
      <form
        className="row g-3"
        onSubmit={(e) => {
          e.preventDefault();
          handleGuardar();
        }}
      >
        <h6 className="mb-2">Disponibilidad</h6>
        <div className="col-12 d-flex align-items-center">
          <div className="form-check">
            <input className="form-check-input" type="checkbox" id="mov-movilizacion" checked={!!movilidad.movilizacion} onChange={(e) => setMovilidad((m) => ({ ...m, movilizacion: e.target.checked }))} />
            <label className="form-check-label" htmlFor="mov-movilizacion">
              Tiene movilizacion propia
            </label>
          </div>
        </div>
        <div className="col-12 d-flex align-items-center">
          <div className="form-check">
            <input className="form-check-input" type="checkbox" id="mov-viajar" checked={!!movilidad.disp_viajar} onChange={(e) => setMovilidad((m) => ({ ...m, disp_viajar: e.target.checked }))} />
            <label className="form-check-label" htmlFor="mov-viajar">
              Disponibilidad para viajar
            </label>
          </div>
        </div>
        <div className="col-12 d-flex align-items-center">
          <div className="form-check">
            <input className="form-check-input" type="checkbox" id="mov-turnos" checked={!!movilidad.disp_turnos} onChange={(e) => setMovilidad((m) => ({ ...m, disp_turnos: e.target.checked }))} />
            <label className="form-check-label" htmlFor="mov-turnos">
              Disponibilidad de turnos
            </label>
          </div>
        </div>
        <div className="col-12 d-flex align-items-center">
          <div className="form-check">
            <input className="form-check-input" type="checkbox" id="mov-fines" checked={!!movilidad.disp_fines_semana} onChange={(e) => setMovilidad((m) => ({ ...m, disp_fines_semana: e.target.checked }))} />
            <label className="form-check-label" htmlFor="mov-fines">
              Disponibilidad fines de semana
            </label>
          </div>
        </div>
        <div className="col-12 text-end">
          <button className="btn btn-primary" type="submit" disabled={saving}>
            {saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </form>
    </div>
  );
  const renderFormacion = () => (
    <div className="row g-3">
      <div className="col-lg-3 col-md-6">
        <label className="form-label">Nivel de estudio</label>
        <select
          className="form-select"
          value={formacionInfo.nivel_estudio || ""}
          onChange={(e) => setFormacionInfo((f) => ({ ...f, nivel_estudio: e.target.value }))}
          disabled={loadingCatalogos}
        >
          <option value="">Selecciona</option>
          {(catalogos.nivel_estudio || []).map((opt) => (
            <option key={`nivel-${opt}`} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </div>
      <div className="col-lg-3 col-md-6">
        <label className="form-label">Institucion</label>
        <input className="form-control" placeholder="Ej: Colegio Nacional Mejia" value={formacionInfo.institucion || ""} onChange={(e) => setFormacionInfo((f) => ({ ...f, institucion: e.target.value }))} />
      </div>
      <div className="col-lg-4 col-md-8">
        <label className="form-label">Titulo obtenido</label>
        <input className="form-control" placeholder="Ej: Bachiller en Ciencias" value={formacionInfo.titulo_obtenido || ""} onChange={(e) => setFormacionInfo((f) => ({ ...f, titulo_obtenido: e.target.value }))} />
      </div>
      <div className="col-lg-2 col-md-4 d-flex align-items-end justify-content-md-end gap-2 formacion-actions-inline">
        {editingEducacionId ? (
          <button
            type="button"
            className="btn btn-outline-secondary formacion-icon-btn"
            onClick={cancelarEdicionEducacion}
            disabled={saving}
            title="Cancelar edicion"
            aria-label="Cancelar edicion"
          >
            <i className="bi bi-x-lg" aria-hidden="true" />
            <span className="visually-hidden">Cancelar edicion</span>
          </button>
        ) : null}
        <button
          type="button"
          className="btn btn-primary formacion-icon-btn"
          onClick={handleGuardarFormacion}
          disabled={saving}
          title={editingEducacionId ? "Actualizar formacion" : "Guardar formacion"}
          aria-label={editingEducacionId ? "Actualizar formacion" : "Guardar formacion"}
        >
          {saving ? (
            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
          ) : (
            <i className="bi bi-floppy" aria-hidden="true" />
          )}
          <span className="visually-hidden">{saving ? "Guardando..." : editingEducacionId ? "Actualizar" : "Guardar"}</span>
        </button>
      </div>
      <div className="col-12">
        <div className="card border-0 shadow-sm">
          <div className="card-body">
            <h6 className="mb-3">Historial de educacion general</h6>
            {educacionGeneralHistorial.length > 0 ? (
              <div className="educacion-general-table-wrapper">
                <div className="d-none d-lg-block">
                  <table className="table table-sm align-middle mb-0 educacion-general-table educacion-general-table-compact">
                    <colgroup>
                      <col style={{ width: "20%" }} />
                      <col style={{ width: "27%" }} />
                      <col style={{ width: "27%" }} />
                      <col style={{ width: "18%" }} />
                      <col style={{ width: "8%" }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th>Nivel</th>
                        <th>Institucion</th>
                        <th>Titulo</th>
                        <th>Registro</th>
                        <th className="text-end">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {educacionGeneralHistorial.map((item, idx) => {
                        const createdAt = item.created_at || item.createdAt || null;
                        const educacionId = item.id;
                        const createdAtLabel = createdAt
                          ? new Date(createdAt).toLocaleString("es-EC", {
                              year: "numeric",
                              month: "2-digit",
                              day: "2-digit",
                            })
                          : "N/D";
                        const nivelText = item.nivel_estudio || item.nivelEstudio || "N/D";
                        const institucionText = item.institucion || "N/D";
                        const tituloText = item.titulo_obtenido || item.tituloObtenido || "N/D";
                        return (
                          <tr key={`edu-hist-${item.id || idx}`}>
                            <td title={nivelText}>
                              <span className="d-inline-block text-truncate w-100">{nivelText}</span>
                            </td>
                            <td title={institucionText}>
                              <span className="d-inline-block text-truncate w-100">{institucionText}</span>
                            </td>
                            <td title={tituloText}>
                              <span className="d-inline-block text-truncate w-100">{tituloText}</span>
                            </td>
                            <td className="text-nowrap">{createdAtLabel}</td>
                            <td className="text-end">
                              <span
                                role="button"
                                tabIndex={saving ? -1 : 0}
                                className={`educacion-menu-icon-trigger${saving ? " is-disabled" : ""}`}
                                onClick={(event) => {
                                  if (saving) return;
                                  toggleEducacionMenu(event, educacionId);
                                }}
                                onKeyDown={(event) => {
                                  if (saving) return;
                                  if (event.key === "Enter" || event.key === " ") {
                                    event.preventDefault();
                                    toggleEducacionMenu(event, educacionId);
                                  }
                                }}
                                title="Acciones"
                                aria-label="Acciones"
                                aria-disabled={saving ? "true" : "false"}
                              >
                                <i className="bi bi-three-dots-vertical" />
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="d-lg-none">
                  {educacionGeneralHistorial.map((item, idx) => {
                    const createdAt = item.created_at || item.createdAt || null;
                    const createdAtLabel = createdAt
                      ? new Date(createdAt).toLocaleString("es-EC", {
                          year: "numeric",
                          month: "2-digit",
                          day: "2-digit",
                        })
                      : "N/D";
                    const nivelText = item.nivel_estudio || item.nivelEstudio || "N/D";
                    const institucionText = item.institucion || "N/D";
                    const tituloText = item.titulo_obtenido || item.tituloObtenido || "N/D";

                    return (
                      <div key={`edu-mobile-${item.id || idx}`} className="border rounded p-2 mb-2 bg-white">
                        <div className="small text-muted">Nivel</div>
                        <div className="fw-semibold mb-2">{nivelText}</div>

                        <div className="small text-muted">Institucion</div>
                        <div className="mb-2">{institucionText}</div>

                        <div className="small text-muted">Titulo</div>
                        <div className="mb-2">{tituloText}</div>

                        <div className="small text-muted">Registro</div>
                        <div className="mb-2">{createdAtLabel}</div>

                        <div className="d-flex justify-content-end gap-2">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleEditarEducacion(item)}
                            disabled={saving}
                          >
                            <i className="bi bi-pencil-square me-1" />
                            Editar
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => abrirEliminarEducacion(item)}
                            disabled={saving}
                          >
                            <i className="bi bi-trash me-1" />
                            Eliminar
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="text-muted small">Sin historial de educacion general.</div>
            )}
          </div>
        </div>
      </div>
      <div className="col-12">
        <HistorialPanel
          seleccionado={estudiante}
          formaciones={historialAcademico.formaciones}
          historial={historialAcademico.historial}
          loading={historialAcademico.loading}
          error={historialAcademico.error}
          canCreate={canCrearFormacion}
          onNuevaFormacion={() =>
            formacionForm.abrirCrear({ estudianteId: id, estudianteNombre: nombreCompleto(estudiante) })
          }
          canEdit={canEditarFormacion}
          canDelete={canEliminarFormacion}
          onEditar={(raw) => formacionForm.abrirEditar(raw)}
          onEliminar={(raw) => formacionForm.abrirEliminar(raw)}
        />
      </div>
    </div>
  );

  const renderExperiencia = () => (
    <form
      className="row g-3"
      onSubmit={(e) => {
        e.preventDefault();
        handleGuardarExperiencia();
      }}
    >
      <div className="col-md-4">
        <label className="form-label">Empresa</label>
        <div className="position-relative">
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder="Busca o crea una empresa"
              value={experienciaEmpresaBusqueda}
              onFocus={() => {
                if (normalizeSearchText(experienciaEmpresaBusqueda).length >= 3) {
                  setExperienciaEmpresaMenuAbierto(true);
                }
              }}
              onBlur={() => {
                setTimeout(() => setExperienciaEmpresaMenuAbierto(false), 150);
              }}
              onChange={(e) => {
                const nextValue = e.target.value;
                setExperienciaEmpresaBusqueda(nextValue);
                const exactMatch = empresas.find(
                  (emp) => normalizeSearchText(emp.nombre) === normalizeSearchText(nextValue)
                );
                setExperiencia((ex) => ({ ...ex, empresa_id: exactMatch?.id || "" }));
                setExperienciaEmpresaMenuAbierto(normalizeSearchText(nextValue).length >= 3);
              }}
              autoComplete="off"
            />
            {!!experienciaEmpresaBusqueda && (
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => {
                  setExperienciaEmpresaBusqueda("");
                  setExperienciaEmpresaMenuAbierto(false);
                  setExperiencia((ex) => ({ ...ex, empresa_id: "" }));
                }}
                title="Limpiar"
              >
                <i className="bi bi-x-lg" />
              </button>
            )}
          </div>
          {experienciaEmpresaMenuAbierto && (
            <div className="list-group exp-empresa-autocomplete shadow-sm">
              {normalizeSearchText(experienciaEmpresaBusqueda).length < 3 ? (
                <div className="list-group-item text-muted small">Ingresa al menos 3 caracteres.</div>
              ) : experienciaEmpresaMatches.length > 0 ? (
                experienciaEmpresaMatches.map((emp) => (
                  <button
                    key={`exp-emp-match-${emp.id}`}
                    type="button"
                    className={`list-group-item list-group-item-action exp-empresa-option ${
                      String(emp.id) === String(experiencia.empresa_id || "") ? "active" : ""
                    }`}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => {
                      setExperiencia((ex) => ({ ...ex, empresa_id: emp.id }));
                      setExperienciaEmpresaBusqueda(emp.nombre || `Empresa ${emp.id}`);
                      setExperienciaEmpresaMenuAbierto(false);
                    }}
                  >
                    {emp.nombre || `Empresa ${emp.id}`}
                  </button>
                ))
              ) : (
                <div className="list-group-item text-muted small">
                  {experienciaEmpresaHasExactMatch
                    ? "Empresa seleccionada."
                    : "Sin coincidencias. Se creara automaticamente al guardar."}
                </div>
              )}
            </div>
          )}
        </div>
        <small className="text-muted d-block mt-1">
          {loadingEmpresas
            ? "Cargando empresas..."
            : "Escribe 3 caracteres para ver coincidencias. Si no existe, se crea al guardar."}
        </small>
      </div>
      <div className="col-md-4">
        <label className="form-label">Cargo</label>
        <input className="form-control" placeholder="Ej: Guardia de seguridad" value={experiencia.cargo || ""} onChange={(e) => setExperiencia((ex) => ({ ...ex, cargo: e.target.value }))} />
      </div>
      <div className="col-md-4">
        <label className="form-label">Tipo de contrato</label>
        <select className="form-select" value={experiencia.tipo_contrato || ""} onChange={(e) => setExperiencia((ex) => ({ ...ex, tipo_contrato: e.target.value }))} disabled={loadingCatalogos}>
          <option value="">Selecciona</option>
          {(catalogos.tipo_contrato || []).map((opt) => (
            <option key={`contr-${opt}`} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </div>
      <div className="col-md-3">
        <label className="form-label">Fecha inicio</label>
        <input type="date" className="form-control" placeholder="AAAA-MM-DD" value={experiencia.fecha_inicio || ""} onChange={(e) => setExperiencia((ex) => ({ ...ex, fecha_inicio: e.target.value }))} />
      </div>
      <div className="col-md-3">
        <label className="form-label">Fecha fin</label>
        <input
          type="date"
          className="form-control"
          placeholder="AAAA-MM-DD"
          value={experiencia.fecha_fin || ""}
          onChange={(e) => setExperiencia((ex) => ({ ...ex, fecha_fin: e.target.value }))}
          disabled={!!experiencia.actualmente_trabaja}
        />
      </div>
      <div className="col-md-3 d-flex align-items-center pt-3">
        <div className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            id="exp-actual"
            checked={!!experiencia.actualmente_trabaja}
            onChange={(e) => {
              const checked = e.target.checked;
              setExperiencia((ex) => ({
                ...ex,
                actualmente_trabaja: checked,
                fecha_fin: checked ? "" : ex.fecha_fin,
                provincia: checked ? ex.provincia : "",
                canton: checked ? ex.canton : "",
              }));
            }}
          />
          <label className="form-check-label" htmlFor="exp-actual">
            Actualmente trabaja
          </label>
        </div>
      </div>
      {!!experiencia.actualmente_trabaja && (
        <>
          <div className="col-md-4">
            <label className="form-label">Provincia donde trabaja</label>
            <select
              className="form-select"
              value={experiencia.provincia || ""}
              onChange={(e) =>
                setExperiencia((ex) => ({
                  ...ex,
                  provincia: e.target.value,
                  canton: "",
                }))
              }
            >
              <option value="">Seleccione provincia</option>
              {provincias.map((prov) => (
                <option key={`exp-prov-${prov.code}`} value={prov.name}>
                  {prov.name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label">Cantón donde trabaja</label>
            <select
              className="form-select"
              value={experiencia.canton || ""}
              onChange={(e) =>
                setExperiencia((ex) => ({ ...ex, canton: e.target.value }))
              }
              disabled={!experiencia.provincia}
            >
              <option value="">Seleccione cantón</option>
              {cantonesExperiencia.map((c) => (
                <option key={`exp-canton-${c.code}-${c.name}`} value={c.name}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        </>
      )}
      <div className="col-md-12">
        <label className="form-label">Descripcion</label>
        <textarea className="form-control" rows={3} placeholder="Describe funciones y logros del cargo" value={experiencia.descripcion || ""} onChange={(e) => setExperiencia((ex) => ({ ...ex, descripcion: e.target.value }))} />
      </div>
      <div className="col-12 d-flex justify-content-end gap-2">
        {editingExperienciaId ? (
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={cancelarEdicionExperiencia}
            disabled={saving}
          >
            Cancelar edicion
          </button>
        ) : null}
        <button className="btn btn-primary" type="submit" disabled={saving}>
          {saving ? "Guardando..." : editingExperienciaId ? "Actualizar" : "Guardar"}
        </button>
      </div>
      <div className="col-12">
        <div className="card border-0 shadow-sm">
          <div className="card-body">
            <h6 className="mb-3">Historial de experiencia laboral</h6>
            {experienciaGeneralHistorial.length > 0 ? (
              <div className="experiencia-general-table-wrapper">
                <div className="d-none d-lg-block">
                  <table className="table table-sm align-middle mb-0 experiencia-general-table">
                    <colgroup>
                      <col style={{ width: "23%" }} />
                      <col style={{ width: "20%" }} />
                      <col style={{ width: "15%" }} />
                      <col style={{ width: "22%" }} />
                      <col style={{ width: "12%" }} />
                      <col style={{ width: "8%" }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th>Empresa</th>
                        <th>Cargo</th>
                        <th>Contrato</th>
                        <th>Periodo</th>
                        <th>Registro</th>
                        <th className="text-end">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {experienciaGeneralHistorial.map((item, idx) => {
                        const createdAt = item.created_at || item.createdAt || null;
                        const experienciaId = item.id;
                        const empresaId = item.empresa_id ?? item.empresaId ?? null;
                        const empresaNombre = empresaId
                          ? (empresas.find((emp) => Number(emp.id) === Number(empresaId))?.nombre || `Empresa #${empresaId}`)
                          : "N/D";
                        const cargoText = item.cargo || "N/D";
                        const contratoText = item.tipo_contrato || "N/D";
                        const fechaInicio = item.fecha_inicio ? String(item.fecha_inicio).split("T")[0] : "N/D";
                        const fechaFin = item.actualmente_trabaja === 1 || item.actualmente_trabaja === "1" || item.actualmente_trabaja === true
                          ? "Actual"
                          : (item.fecha_fin ? String(item.fecha_fin).split("T")[0] : "N/D");
                        const periodoText = `${fechaInicio} - ${fechaFin}`;
                        const createdAtLabel = createdAt
                          ? new Date(createdAt).toLocaleString("es-EC", {
                              year: "numeric",
                              month: "2-digit",
                              day: "2-digit",
                            })
                          : "N/D";

                        return (
                          <tr key={`exp-hist-${item.id || idx}`}>
                            <td title={empresaNombre}>
                              <span className="d-inline-block text-truncate w-100">{empresaNombre}</span>
                            </td>
                            <td title={cargoText}>
                              <span className="d-inline-block text-truncate w-100">{cargoText}</span>
                            </td>
                            <td title={contratoText}>
                              <span className="d-inline-block text-truncate w-100">{contratoText}</span>
                            </td>
                            <td title={periodoText}>
                              <span className="d-inline-block text-truncate w-100">{periodoText}</span>
                            </td>
                            <td className="text-nowrap">{createdAtLabel}</td>
                            <td className="text-end">
                              <button
                                type="button"
                                className="experiencia-menu-trigger"
                                onClick={(event) => toggleExperienciaMenu(event, experienciaId)}
                                title="Acciones"
                                disabled={saving}
                              >
                                <i className="bi bi-three-dots-vertical" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="d-lg-none">
                  {experienciaGeneralHistorial.map((item, idx) => {
                    const empresaId = item.empresa_id ?? item.empresaId ?? null;
                    const empresaNombre = empresaId
                      ? (empresas.find((emp) => Number(emp.id) === Number(empresaId))?.nombre || `Empresa #${empresaId}`)
                      : "N/D";
                    const cargoText = item.cargo || "N/D";
                    const contratoText = item.tipo_contrato || "N/D";
                    const fechaInicio = item.fecha_inicio ? String(item.fecha_inicio).split("T")[0] : "N/D";
                    const fechaFin = item.actualmente_trabaja === 1 || item.actualmente_trabaja === "1" || item.actualmente_trabaja === true
                      ? "Actual"
                      : (item.fecha_fin ? String(item.fecha_fin).split("T")[0] : "N/D");
                    const createdAtLabel = item.created_at
                      ? new Date(item.created_at).toLocaleString("es-EC", {
                          year: "numeric",
                          month: "2-digit",
                          day: "2-digit",
                        })
                      : "N/D";

                    return (
                      <div key={`exp-mobile-${item.id || idx}`} className="border rounded p-2 mb-2 bg-white">
                        <div className="small text-muted">Empresa</div>
                        <div className="fw-semibold mb-2">{empresaNombre}</div>

                        <div className="small text-muted">Cargo</div>
                        <div className="mb-2">{cargoText}</div>

                        <div className="small text-muted">Contrato</div>
                        <div className="mb-2">{contratoText}</div>

                        <div className="small text-muted">Periodo</div>
                        <div className="mb-2">{fechaInicio} - {fechaFin}</div>

                        <div className="small text-muted">Registro</div>
                        <div className="mb-2">{createdAtLabel}</div>

                        <div className="d-flex justify-content-end gap-2">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleEditarExperiencia(item)}
                            disabled={saving}
                          >
                            <i className="bi bi-pencil-square me-1" />
                            Editar
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => abrirEliminarExperiencia(item)}
                            disabled={saving}
                          >
                            <i className="bi bi-trash me-1" />
                            Eliminar
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="text-muted small">Sin historial de experiencia laboral.</div>
            )}
          </div>
        </div>
      </div>
    </form>
  );

  const renderSalud = () => (
    <form
      className="row g-3"
      onSubmit={(e) => {
        e.preventDefault();
        // Tipo de sangre y tatuaje son independientes — cada uno se guarda
        // con lo que tenga, sin exigir el otro (antes bloqueaba el guardado
        // si faltaba cualquiera de los dos).
        handleGuardar();
      }}
    >
       <div className="col-md-4">
        <label className="form-label">Sexo</label>
        <select className="form-select" value={perfil.sexo || ""} onChange={(e) => setPerfil((p) => ({ ...p, sexo: e.target.value }))} disabled={loadingCatalogos}>
          <option value="">Selecciona</option>
          {(catalogos.sexo || []).map((opt) => (
            <option key={`sexo-${opt}`} value={opt}>
              {SEXO_LABELS[opt] || opt}
            </option>
          ))}
        </select>
      </div>
      <div className="col-md-4">
        <div className="d-flex justify-content-between align-items-center mb-1">
          <label className="form-label mb-0">Tipo de sangre</label>
          {hasCarnetTipoSangre && <span className="badge bg-success">Carnet subido</span>}
        </div>
        <select
          className="form-select"
          value={salud.tipo_sangre || ""}
          onChange={(e) => setSalud((s) => ({ ...s, tipo_sangre: e.target.value }))}
          disabled={loadingCatalogos}
        >
          <option value="">Selecciona</option>
          {(catalogos.tipo_sangre || []).map((opt) => (
            <option key={`ts-${opt}`} value={opt}>
              {opt}
            </option>
          ))}
        </select>
        {!hasCarnetTipoSangre && (
          <div className="mt-2">
            <button
              type="button"
              className="btn btn-outline-primary w-100"
              onClick={handleCarnetUpload}
              disabled={!salud.tipo_sangre || documentosData.uploading || !estudiante}
            >
              {documentosData.uploading ? "Subiendo..." : "Subir carnet"}
            </button>
          </div>
        )}
        <input
          type="file"
          accept={DOCUMENTOS_EXT_PERMITIDAS.join(",")}
          ref={carnetFileRef}
          style={{ display: "none" }}
          onChange={onCarnetFileChange}
        />
      </div>
      <div className="col-md-4">
        <label className="form-label">Estatura (m)</label>
        <input className="form-control" placeholder="Ej: 1.70" value={salud.estatura || ""} onChange={(e) => setSalud((s) => ({ ...s, estatura: e.target.value }))} />
      </div>
      <div className="col-md-4">
        <label className="form-label">Peso (kg)</label>
        <input className="form-control" placeholder="Ej: 70" value={salud.peso || ""} onChange={(e) => setSalud((s) => ({ ...s, peso: e.target.value }))} />
      </div>
      <div className="col-md-4">
        <label className="form-label">Tatuaje</label>
        <select className="form-select" value={salud.tatuaje || ""} onChange={(e) => setSalud((s) => ({ ...s, tatuaje: e.target.value }))} disabled={loadingCatalogos}>
          <option value="">Selecciona</option>
          {(catalogos.tatuaje || []).map((opt) => (
            <option key={`tat-${opt}`} value={opt}>
              {opt.replace('_', ' ')}
            </option>
          ))}
        </select>
      </div>
      <div className="col-12 text-end">
        <button className="btn btn-primary" type="submit" disabled={saving}>
          {saving ? "Guardando..." : "Guardar"}
        </button>
      </div>
    </form>
  );
  if (loading) {
    return (
      <div className="doc-page container-fluid px-3 py-3">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status" />
        </div>
      </div>
    );
  }

  if (error || !estudiante) {
    return (
      <div className="doc-page container-fluid px-3 py-3">
        <div className="alert alert-danger d-flex justify-content-between align-items-center">
          <div>{error || "No se pudo cargar el estudiante."}</div>
          <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate(-1)}>
            Volver
          </button>
        </div>
      </div>
    );
  }

  const handleBack = () => {
    const canGoBack = typeof window !== "undefined" && window.history.length > 2;
    if (canGoBack) {
      navigate(-1);
    } else {
      navigate("/dashboard/view_dashboard");
    }
  };

  const educacionQuickMenu =
    educacionMenuOpenId && educacionMenuPosition && educacionMenuItem && typeof document !== "undefined"
      ? createPortal(
          <div
            ref={educacionMenuRef}
            className="educacion-quick-menu"
            style={{ top: `${educacionMenuPosition.top}px`, left: `${educacionMenuPosition.left}px` }}
            role="menu"
            aria-label="Acciones de educacion"
          >
            <button
              type="button"
              className="menu-item"
              onClick={() => handleEditarEducacion(educacionMenuItem)}
              disabled={saving}
            >
              <i className="bi bi-pencil" />
              Editar
            </button>
            <button
              type="button"
              className="menu-item danger"
              onClick={() => abrirEliminarEducacion(educacionMenuItem)}
              disabled={saving}
            >
              <i className="bi bi-trash" />
              Eliminar
            </button>
          </div>,
          document.body
        )
      : null;
  const experienciaQuickMenu =
    experienciaMenuOpenId && experienciaMenuPosition && experienciaMenuItem && typeof document !== "undefined"
      ? createPortal(
          <div
            ref={experienciaMenuRef}
            className="educacion-quick-menu"
            style={{ top: `${experienciaMenuPosition.top}px`, left: `${experienciaMenuPosition.left}px` }}
            role="menu"
            aria-label="Acciones de experiencia"
          >
            <button
              type="button"
              className="menu-item"
              onClick={() => handleEditarExperiencia(experienciaMenuItem)}
              disabled={saving}
            >
              <i className="bi bi-pencil" />
              Editar
            </button>
            <button
              type="button"
              className="menu-item danger"
              onClick={() => abrirEliminarExperiencia(experienciaMenuItem)}
              disabled={saving}
            >
              <i className="bi bi-trash" />
              Eliminar
            </button>
          </div>,
          document.body
        )
      : null;

  return (
    <div className="doc-page container-fluid px-3 py-3 estudiantes-wrapper">
      <div className="doc-breadcrumb">
        <Link to="/dashboard/estudiantes">Estudiantes</Link> / Detalle
      </div>
      <div className="doc-header-card mb-3">
        <div className="doc-header-top">
          <div className="doc-header-main">
            <div className="doc-title-row">
              <h4 className="mb-0">{nombreCompleto(estudiante) || "Estudiante"}</h4>
              <span className="doc-badge badge-activo">{estudiante.activo ? "Activo" : "Inactivo"}</span>
              <span className="doc-badge badge-inscrito">{estudiante.estadoAcademico || "Inscrito"}</span>
            </div>
            <div className="d-flex flex-wrap gap-3 text-muted small">
              <span>
                <i className="bi bi-credit-card-2-front me-1" />
                {estudiante.documento || "N/D"}
              </span>
              <span>
                <i className="bi bi-envelope me-1" />
                {estudiante.email || "Sin email"}
              </span>
              <span>
                <i className="bi bi-phone me-1" />
                {estudiante.telefono || "Sin telefono"}
              </span>
            </div>
          </div>
          <button
            type="button"
            className="btn btn-light d-inline-flex align-items-center gap-2 doc-back-btn"
            onClick={handleBack}
          >
            <i className="bi bi-arrow-left"></i>
            Volver
          </button>
        </div>
      </div>

      <div className="card shadow-sm mb-3">
        <div className="card-header border-0">
          <ul className="nav nav-tabs card-header-tabs doc-tabs">
            <li className="nav-item">
              <button className={`nav-link ${tab === "perfil" ? "active" : ""}`} onClick={() => setTabAndQuery("perfil")}>
                <i className="bi bi-person-lines-fill me-1" /> Perfil
              </button>
            </li>
            <li className="nav-item">
              <button className={`nav-link ${tab === "domicilio" ? "active" : ""}`} onClick={() => setTabAndQuery("domicilio")}>
                <i className="bi bi-geo-alt me-1" /> Domicilio
              </button>
            </li>
            <li className="nav-item">
              <button className={`nav-link ${tab === "movilidad" ? "active" : ""}`} onClick={() => setTabAndQuery("movilidad")}>
                <i className="bi bi-truck me-1" /> Movilidad
              </button>
            </li>
            <li className="nav-item">
              <button className={`nav-link ${tab === "salud" ? "active" : ""}`} onClick={() => setTabAndQuery("salud")}>
                <i className="bi bi-heart-pulse me-1" /> Salud
              </button>
            </li>
            <li className="nav-item">
              <button className={`nav-link ${tab === "formacion" ? "active" : ""}`} onClick={() => setTabAndQuery("formacion")}>
                <i className="bi bi-mortarboard me-1" /> Formacion
              </button>
            </li>
            <li className="nav-item">
              <button className={`nav-link ${tab === "experiencia" ? "active" : ""}`} onClick={() => setTabAndQuery("experiencia")}>
                <i className="bi bi-briefcase me-1" /> Experiencia
              </button>
            </li>
            <li className="nav-item">
              <button className={`nav-link ${tab === "documentos" ? "active" : ""}`} onClick={() => setTabAndQuery("documentos")}>
                <i className="bi bi-folder2-open me-1" /> Documentos
              </button>
            </li>
            <li className="nav-item">
              <button className={`nav-link ${tab === "entregables" ? "active" : ""}`} onClick={() => setTabAndQuery("entregables")}>
                <i className="bi bi-box-seam me-1" /> Entregables
              </button>
            </li>
          </ul>
        </div>
        <div className="card-body">
          {tab === "perfil" && renderPerfil()}
          {tab === "domicilio" && renderDomicilio()}
          {tab === "movilidad" && renderMovilidad()}
          {tab === "salud" && renderSalud()}
          {tab === "formacion" && renderFormacion()}
          {tab === "experiencia" && renderExperiencia()}
          {tab === "documentos" && <DocumentosTab estudianteId={id} estudiante={estudiante} documentosData={documentosData} />}
          {tab === "entregables" && (
            <EntregablesEstudianteTab
              estudianteId={id}
              matriculaIdInicial={new URLSearchParams(location?.search || "").get("matriculaId")}
            />
          )}
        </div>
      </div>

      {educacionToDelete ? (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(15, 23, 42, 0.45)" }}
          aria-modal="true"
          role="dialog"
        >
          <div className="modal-dialog modal-dialog-centered modal-md" role="document">
            <div className="modal-content">
              <div className="modal-header border-0">
                <h5 className="modal-title">Eliminar registro de educacion</h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Cerrar"
                  onClick={() => setEducacionToDelete(null)}
                  disabled={saving}
                />
              </div>
              <div className="modal-body pt-0">
                <p className="mb-2">
                  Estas a punto de eliminar el registro de <strong>{educacionToDelete.nivel_estudio || educacionToDelete.nivelEstudio || "educacion"}</strong>.
                </p>
                <p className="text-muted mb-0">Esta accion no se puede deshacer.</p>
              </div>
              <div className="modal-footer border-0 pt-0">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setEducacionToDelete(null)}
                  disabled={saving}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmarEliminarEducacion}
                  disabled={saving}
                >
                  {saving ? "Eliminando..." : "Eliminar"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
      {experienciaToDelete ? (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(15, 23, 42, 0.45)" }}
          aria-modal="true"
          role="dialog"
        >
          <div className="modal-dialog modal-dialog-centered modal-md" role="document">
            <div className="modal-content">
              <div className="modal-header border-0">
                <h5 className="modal-title">Eliminar registro de experiencia</h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Cerrar"
                  onClick={() => setExperienciaToDelete(null)}
                  disabled={saving}
                />
              </div>
              <div className="modal-body pt-0">
                <p className="mb-2">
                  Estas a punto de eliminar el registro de <strong>{experienciaToDelete.cargo || "experiencia"}</strong>.
                </p>
                <p className="text-muted mb-0">Esta accion no se puede deshacer.</p>
              </div>
              <div className="modal-footer border-0 pt-0">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setExperienciaToDelete(null)}
                  disabled={saving}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmarEliminarExperiencia}
                  disabled={saving}
                >
                  {saving ? "Eliminando..." : "Eliminar"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
      {educacionQuickMenu}
      {experienciaQuickMenu}

      <FormacionFormModal
        {...formacionForm}
        matriculasOptions={formacionMatriculasOptions}
        cursosOptions={formacionCursosOptions}
        loadingOptions={formacionLoadingOptions}
        reloadOptions={formacionReloadOptions}
      />
    </div>
  );
}

